import { ArrowLeft } from "lucide-react";

const Section = ({ num, title, children }) => (
  <div className="mb-10">
    <h2 className="font-outfit font-black text-white text-lg mb-4 pb-3 border-b border-white/8">
      <span className="text-[#3B9EFF] mr-2">{num}.</span>{title}
    </h2>
    <div className="space-y-3 text-[#A1A1AA] text-sm font-inter leading-relaxed">{children}</div>
  </div>
);

const Sub = ({ children }) => (
  <h3 className="text-white font-semibold text-sm pt-2">{children}</h3>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1.5">
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </ul>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#0C0E13]">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#0A0C10]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-medium group">
            <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
            Back to Fanible
          </a>
          <div className="flex items-center gap-2">
            <img src="/fanible-icon.png" alt="Fanible" className="w-6 h-6 rounded-lg" style={{ mixBlendMode: "screen" }} />
            <span className="font-outfit font-black text-white text-sm uppercase tracking-tight">Fanible</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">

        {/* Title block */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 rounded-full px-3 py-1.5 mb-5">
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#3B9EFF]">Legal</span>
          </div>
          <h1 className="font-outfit font-black text-white text-4xl sm:text-5xl tracking-tight mb-4">Privacy Policy</h1>
          <p className="text-[#A1A1AA] text-sm font-inter">Last Updated: August 27, 2026</p>
        </div>

        {/* Introduction */}
        <div className="mb-10 p-6 bg-white/[0.03] border border-white/8 rounded-2xl space-y-3 text-[#A1A1AA] text-sm font-inter leading-relaxed">
          <p>
            Fanible Private Limited ("Fanible", "we", "our", or "us") respects your privacy and is committed to
            protecting the personal information of users of the Fanible mobile application, website, products,
            services, and related features (collectively, the "Platform").
          </p>
          <p>
            This Privacy Policy explains what personal information Fanible collects, how we use it, how we store
            and protect it, when we share it, how long we retain it, and the choices available to you.
          </p>
          <p>
            By using Fanible, creating an account, or otherwise interacting with the Platform, you acknowledge
            that you have read and understood this Privacy Policy.
          </p>
          <p className="text-white font-semibold">Fanible is intended for users 18 years of age or older.</p>
        </div>

        <Section num="1" title="About Fanible">
          <p>
            Fanible is a sports-focused social platform operated by Fanible Private Limited, a company
            registered in Samastipur, Bihar, India.
          </p>
          <p>
            The Platform allows users to participate in sports discussions, publish sports-related posts, share
            sports news and articles, upload sports-related images, follow creators, vote and interact with
            content, comment on posts, and participate in eligible creator-support and monetization features.
          </p>
          <p>
            Fanible's current services may include user-generated content, creator Shoutouts, creator earnings,
            and related platform functionality.
          </p>
        </Section>

        <Section num="2" title="Information We Collect">
          <p>
            We collect information that is necessary to create and operate your Fanible account, provide
            Platform functionality, process transactions, maintain security, and provide our services. The
            categories of information we may collect include the following.
          </p>

          <Sub>2.1 Account and Profile Information</Sub>
          <p>Depending on how you create and use your account, we may collect:</p>
          <Bullets items={[
            "Name", "Username", "Email address", "Phone number", "Profile picture",
            "Account authentication information", "Information associated with your Fanible profile",
          ]} />
          <p>You may provide different information depending on the registration method available to you.</p>

          <Sub>2.2 Google Sign-In Information</Sub>
          <p>
            If you choose to create or access your Fanible account using Google Sign-In, we may receive
            information made available to us by Google, which may include:
          </p>
          <Bullets items={[
            "Your Google account name", "Email address", "Profile picture",
            "Information necessary to authenticate your account",
          ]} />
          <p>
            We use this information to create, authenticate, and maintain your Fanible account. Fanible does
            not request or collect your Google password. Your use of Google services is also subject to Google's
            applicable terms and privacy policies.
          </p>

          <Sub>2.3 Phone OTP Authentication</Sub>
          <p>
            Fanible may allow you to authenticate your account using a one-time password (OTP) sent to your
            phone number. When you use phone OTP authentication, we may collect and store your phone number
            and information necessary to verify your authentication request. OTP credentials are used for
            authentication and account security and are not used by Fanible as a password.
          </p>

          <Sub>2.4 Username and Password Accounts</Sub>
          <p>
            Where username and password registration is available, Fanible may collect your username and
            password credentials. Passwords are not intended to be stored in plain-text form. Fanible uses
            appropriate security measures designed to protect account credentials.
          </p>
          <p>
            You are responsible for maintaining the security of your account credentials and for notifying
            Fanible if you believe your account has been accessed without authorization.
          </p>
        </Section>

        <Section num="3" title="User-Generated Content">
          <p>
            Fanible is a social platform and therefore collects and stores content that you voluntarily submit
            or publish through the Platform. This may include:
          </p>
          <Bullets items={[
            "Text posts", "Sports news", "Sports blogs", "Sports articles", "Sports-related images",
            "Comments", "Votes", "Likes and other interactions", "Follow relationships",
            "Creator-related content", "Shoutout messages or related information",
            "Other information you voluntarily submit through Fanible's features",
          ]} />
          <p>
            Content that you publish on Fanible may be visible to other users depending on the applicable
            Platform functionality. You remain responsible for the content you submit and must have the
            necessary rights and permissions to publish it.
          </p>
          <p>
            Fanible may process user-generated content for purposes including providing the Platform,
            displaying content to other users, moderation, safety, security, enforcement of our Terms and
            Conditions, and preventing misuse of the Platform.
          </p>
          <p>Fanible currently does not provide a private direct-messaging/chat system between users.</p>
        </Section>

        <Section num="4" title="Camera and Photos/Media">
          <p>
            Fanible may request access to your camera or photos/media on your device when necessary for
            features that allow you to create or upload content. For example, you may choose to:
          </p>
          <Bullets items={[
            "Take a photograph for your profile or a post",
            "Select an image or other supported media for upload",
          ]} />
          <p>
            Fanible does not access your camera or personal media merely for unrelated purposes. Where Android
            provides a system-level photo or media selection mechanism, Fanible may use the permissions and
            mechanisms supported by the operating system for the applicable functionality.
          </p>
          <p>
            You control whether you provide content through these features, subject to the permissions and
            controls provided by your device.
          </p>
        </Section>

        <Section num="5" title="Push Notifications">
          <p>
            Fanible may use push notification services to send notifications related to your account and
            activity on the Platform. Notifications may include information such as:
          </p>
          <Bullets items={[
            "Account-related notifications", "Interactions with your content", "Comments or other engagement",
            "Creator-related activity", "Shoutout or transaction-related notifications",
            "Security or service notifications", "Other important Platform notifications",
          ]} />
          <p>
            A push notification service may process a device/app notification token or similar technical
            information necessary to deliver notifications. You may manage notification permissions through
            your device settings, although disabling notifications may prevent you from receiving certain
            Platform notifications.
          </p>
        </Section>

        <Section num="6" title="Information We Do Not Intentionally Collect">
          <p>
            Based on Fanible's current functionality, Fanible does not intentionally collect the following
            information for analytics, advertising, profiling, or other unrelated purposes:
          </p>
          <Bullets items={[
            "Precise location", "Approximate location", "Contacts/address book",
            "Advertising ID for advertising purposes", "Device model for profiling",
            "Android version for profiling", "IP address as a user profile field", "Location history",
            "Browsing history outside Fanible", "Microphone recordings",
            "Private messages (Fanible currently does not provide a private messaging system)",
            "Advertising or analytics profiles",
          ]} />
          <p>
            Fanible currently does not display third-party advertisements and does not currently use
            third-party analytics services for user analytics.
          </p>
          <p className="text-white font-semibold">
            Important: This section describes Fanible's intended data practices and does not override
            information that may be technically processed by infrastructure or service providers as necessary
            to operate, secure, or deliver the Platform.
          </p>
        </Section>

        <Section num="7" title="Creator Program and Verification Information">
          <p>
            Eligible users may participate in Fanible's Creator Program. To enable creator monetization,
            withdrawals, fraud prevention, verification, and compliance with applicable requirements, Fanible
            may collect additional information from eligible creators. Depending on the applicable withdrawal
            or verification process, this may include:
          </p>
          <Bullets items={[
            "UPI ID", "Bank account number", "IFSC", "Account holder name",
            "PAN information", "Aadhaar information", "KYC or verification information",
          ]} />
          <p>
            We collect such information only when necessary for the applicable creator, withdrawal,
            verification, legal, tax, fraud-prevention, or security purposes. Because financial and identity
            information can be sensitive, Fanible applies additional safeguards to information used for
            creator payments and verification.
          </p>
        </Section>

        <Section num="8" title="Payments and Razorpay">
          <p>
            Fanible uses Razorpay as a payment service provider for applicable Shoutout transactions. When
            you make a payment, sensitive payment information such as card or other payment credentials may
            be entered directly with Razorpay or processed by Razorpay according to its applicable policies
            and security practices.
          </p>
          <p>
            Fanible does not intentionally store full payment card numbers or other sensitive payment
            credentials that are handled directly by Razorpay. Fanible may receive and retain information
            necessary to record and manage a transaction, which may include:
          </p>
          <Bullets items={[
            "Transaction information", "Payment status", "Transaction/reference identifiers", "Amount",
            "Sender and recipient information",
            "Information necessary for refunds, disputes, accounting, fraud prevention, and support",
          ]} />
          <p>Your use of Razorpay's services may also be governed by Razorpay's own terms and privacy policy.</p>
        </Section>

        <Section num="9" title="AWS Infrastructure">
          <p>
            Fanible uses Amazon Web Services (AWS) infrastructure to operate and deliver parts of the
            Platform. Our current AWS infrastructure includes services such as:
          </p>
          <Bullets items={[
            "Amazon S3", "AWS Lambda", "Amazon API Gateway",
            "Amazon DynamoDB", "Amazon DocumentDB", "Amazon CloudFront",
          ]} />
          <p>These services may be used for functions including:</p>
          <Bullets items={[
            "Storing application and user data", "Storing uploaded media", "Running backend services",
            "Processing application requests", "Database storage", "Delivering content",
            "Maintaining Platform functionality and security",
          ]} />
          <p>
            Information stored or processed through AWS is handled for the purposes described in this Privacy
            Policy. Fanible uses appropriate technical and organizational safeguards intended to protect
            information processed through its infrastructure.
          </p>
        </Section>

        <Section num="10" title="How We Use Your Information">
          <p>Fanible may use collected information for the following purposes:</p>

          <Sub>Providing the Platform</Sub>
          <Bullets items={[
            "Create and manage your account", "Authenticate users", "Maintain your profile",
            "Provide social and community features", "Display your content",
            "Allow you to interact with other users", "Enable creator features",
            "Deliver notifications", "Provide customer support",
          ]} />

          <Sub>Platform Security</Sub>
          <Bullets items={[
            "Detect unauthorized account access", "Prevent fraud", "Detect scams", "Investigate abuse",
            "Protect users and the Platform", "Enforce our Terms and Conditions",
            "Detect suspicious activity", "Prevent manipulation of Platform features",
            "Protect payment and creator-earning systems",
          ]} />

          <Sub>Payments and Financial Administration</Sub>
          <Bullets items={[
            "Process Shoutout transactions", "Record payments", "Facilitate creator withdrawals",
            "Verify eligible creators", "Prevent payment fraud", "Resolve payment disputes",
            "Maintain financial and accounting records", "Comply with applicable legal obligations",
          ]} />

          <Sub>Legal and Regulatory Compliance</Sub>
          <Bullets items={[
            "Comply with applicable laws", "Respond to lawful requests",
            "Establish, exercise, or defend legal claims", "Investigate suspected unlawful activity",
            "Meet accounting, tax, regulatory, or legal obligations",
            "Protect the rights, property, safety, and security of Fanible and its users",
          ]} />
        </Section>

        <Section num="11" title="User Content and Public Information">
          <p>
            When you voluntarily publish content on Fanible, information associated with that content may
            become visible to other users. Depending on the feature, this may include:
          </p>
          <Bullets items={[
            "Your username", "Profile picture", "Public profile information", "Posts", "Images",
            "Comments", "Votes or other interactions", "Creator information", "Public Shoutout-related information",
          ]} />
          <p>
            You should therefore avoid publishing information that you do not want other Fanible users to see.
          </p>
          <p>
            Fanible does not guarantee that content shared publicly can be completely removed from copies,
            screenshots, or other information independently created by other users.
          </p>
        </Section>

        <Section num="12" title="Content Moderation and Safety">
          <p>
            Fanible may review, process, restrict, remove, or retain user-generated content for purposes
            including:
          </p>
          <Bullets items={[
            "Enforcing community standards", "Preventing prohibited content", "Protecting users",
            "Investigating reports", "Detecting fraud and scams", "Preventing illegal activities",
            "Protecting intellectual property rights", "Maintaining Platform security",
            "Complying with applicable laws",
          ]} />
          <p>
            Fanible may suspend or terminate accounts involved in serious violations. Our Terms and
            Conditions contain additional information regarding content moderation and enforcement.
          </p>
        </Section>

        <Section num="13" title="Information Sharing">
          <p>
            Fanible does not sell your personal information. We may share information with third parties
            only where reasonably necessary for the purposes described in this Privacy Policy.
          </p>

          <Sub>Service Providers</Sub>
          <p>
            We may use service providers that process information on our behalf, including infrastructure,
            hosting, content delivery, authentication, notification, payment, verification, and other
            technology providers.
          </p>

          <Sub>Payment Providers</Sub>
          <p>
            Razorpay may process payment information necessary to complete and manage applicable transactions.
          </p>

          <Sub>Cloud and Infrastructure Providers</Sub>
          <p>AWS may process and store information necessary for Fanible's operation.</p>

          <Sub>Legal and Regulatory Authorities</Sub>
          <p>
            Fanible may disclose information where required or permitted by applicable law, including in
            response to lawful requests or where necessary to prevent or investigate fraud, scams, illegal
            activities, security incidents, or other serious misuse.
          </p>

          <Sub>Business Transactions</Sub>
          <p>
            If Fanible is involved in a merger, acquisition, restructuring, financing, sale of assets, or
            similar transaction, information may be transferred as part of that transaction, subject to
            applicable law and appropriate safeguards.
          </p>
        </Section>

        <Section num="14" title="Third-Party Authentication and Services">
          <p>Fanible may integrate with third-party services such as:</p>
          <Bullets items={[
            "Google Sign-In", "Phone OTP/SMS authentication providers", "Razorpay",
            "Amazon Web Services", "Push notification providers",
            "Other service providers required to operate Platform features",
          ]} />
          <p>
            These third parties may process information according to their own privacy policies and
            applicable contractual arrangements. Fanible does not control the privacy practices of
            independent third-party services.
          </p>
        </Section>

        <Section num="15" title="Data Security">
          <p>
            Fanible takes reasonable technical and organizational measures designed to protect personal
            information against unauthorized access, alteration, disclosure, destruction, loss, or misuse.
            Depending on the nature of the information and the applicable service, safeguards may include:
          </p>
          <Bullets items={[
            "Encryption of information during transmission", "Access controls", "Authentication controls",
            "Secure backend infrastructure", "Restricted access to sensitive information",
            "Secure storage practices", "Monitoring and security measures",
            "Measures designed to prevent unauthorized access",
          ]} />
          <p>
            However, no method of transmitting or storing information over the Internet can be guaranteed to
            be completely secure. You are also responsible for maintaining the security of your account
            credentials and for notifying Fanible if you suspect unauthorized access.
          </p>
        </Section>

        <Section num="16" title="Data Retention">
          <p>
            Fanible retains personal information only for as long as reasonably necessary for the purposes
            described in this Privacy Policy, including providing the Platform, maintaining security,
            fulfilling legal obligations, resolving disputes, preventing fraud, maintaining financial records,
            and enforcing our Terms and Conditions.
          </p>
          <p>
            When information is no longer required for these purposes, Fanible will take reasonable steps to
            delete, anonymize, or otherwise dispose of it, subject to applicable legal and legitimate
            business requirements.
          </p>

          <Sub>Fraud, Scams, Abuse and Security</Sub>
          <p>
            If an account is reasonably determined to have been involved in serious fraud, scams, payment
            abuse, illegal activity, security abuse, or other significant violations, Fanible may retain
            limited information necessary to prevent repeated abuse and protect the Platform and its users.
            Such retained information may include limited account identifiers such as:
          </p>
          <Bullets items={[
            "User/account ID", "Name",
            "Other information reasonably necessary for fraud prevention or legal compliance",
          ]} />
          <p>
            Fanible will not retain information indefinitely merely because an account previously existed.
            Retention will be limited to what is reasonably necessary for the applicable purpose or required
            by law.
          </p>
        </Section>

        <Section num="17" title="Account Deletion">
          <p>You may request deletion of your Fanible account and associated personal information.</p>
          <p>Fanible will provide an in-app account deletion option through:</p>
          <p className="text-white font-semibold">Settings → Account → Delete Account</p>
          <p>Fanible will also provide an external web-based mechanism for account deletion:</p>
          <p>
            Account Deletion Page:{" "}
            <a href="/delete-account" className="text-[#3B9EFF] hover:underline font-medium">https://www.fanible.com/delete-account</a>
          </p>
          <p>This external deletion resource is publicly accessible without requiring a Fanible login.</p>
          <p>
            You may also contact Fanible at{" "}
            <a href="mailto:hello@fanible.com" className="text-[#3B9EFF] hover:underline">hello@fanible.com</a>.
          </p>
          <p>
            After receiving and verifying a deletion request, Fanible will initiate the deletion process.
            Fanible's Terms and Conditions currently provide that account deletion requests are initiated
            within 24 hours after successful verification.
          </p>
          <p>
            Account deletion may permanently remove access to your account, profile, posts, interactions,
            creator features, and other account-associated services.
          </p>
          <p>
            Where required by applicable law, or where reasonably necessary for fraud prevention, security,
            legal compliance, tax/accounting requirements, dispute resolution, or protection of legal rights,
            Fanible may retain limited information for the applicable retention period. Where information is
            legitimately retained after account deletion, Fanible will restrict its use to the purpose for
            which retention is necessary.
          </p>
        </Section>

        <Section num="18" title="What Happens to Your User Content After Account Deletion">
          <p>
            When your account is deleted, Fanible will take reasonable steps to delete or anonymize personal
            information associated with the account, subject to legitimate retention requirements described
            in this Privacy Policy.
          </p>
          <p>Some content or information may be retained where necessary for:</p>
          <Bullets items={[
            "Legal compliance", "Fraud prevention", "Security", "Dispute resolution",
            "Enforcement of Platform policies", "Protection of legal rights",
            "Backup or technical recovery processes for a limited period",
          ]} />
          <p>
            Where technically or legally appropriate, retained information will not be used for ordinary
            Platform purposes after account deletion.
          </p>
        </Section>

        <Section num="19" title="Your Privacy Rights and Choices">
          <p>
            Depending on applicable law, you may have rights relating to your personal information, which may
            include:
          </p>
          <Bullets items={[
            "Requesting access to personal information we hold about you",
            "Requesting correction of inaccurate information",
            "Requesting deletion of your account and associated information",
            "Withdrawing consent where processing is based on consent",
            "Requesting information about how your data is processed",
            "Raising a privacy-related complaint or concern",
          ]} />
          <p>
            You may exercise applicable rights by contacting:{" "}
            <a href="mailto:hello@fanible.com" className="text-[#3B9EFF] hover:underline">hello@fanible.com</a>
          </p>
          <p>
            We may need to verify your identity before processing certain requests in order to protect your
            account and personal information.
          </p>
          <p>
            Where a request cannot be fully fulfilled because information must legally or legitimately be
            retained, Fanible will explain the applicable limitation where required by law.
          </p>
        </Section>

        <Section num="20" title="Children's Privacy">
          <p>
            Fanible is intended for individuals who are 18 years of age or older. Fanible does not knowingly
            permit individuals under 18 to create or maintain accounts where prohibited by applicable law or
            Platform requirements.
          </p>
          <p>
            If we become aware that an individual under the applicable minimum age has provided personal
            information to Fanible in violation of our requirements, we may take appropriate steps to remove
            the account and associated information, subject to applicable law.
          </p>
          <p>
            If you believe a person under the applicable minimum age has created an account on Fanible, please
            contact us at{" "}
            <a href="mailto:hello@fanible.com" className="text-[#3B9EFF] hover:underline">hello@fanible.com</a>.
          </p>
        </Section>

        <Section num="21" title="International Data Processing">
          <p>
            Fanible is operated by Fanible Private Limited in India and uses third-party infrastructure and
            service providers that may process information in locations outside India.
          </p>
          <p>
            For example, Fanible uses Amazon Web Services and other third-party providers to operate its
            Platform. Where personal information is processed outside your jurisdiction, Fanible will take
            reasonable steps to comply with applicable legal requirements relating to such processing.
          </p>
        </Section>

        <Section num="22" title="Data Breaches and Security Incidents">
          <p>
            Fanible maintains security measures designed to protect user information. If Fanible becomes aware
            of a security incident involving personal information, Fanible may investigate the incident and
            take reasonable steps to contain, mitigate, and address it.
          </p>
          <p>
            Where notification is required by applicable law, Fanible will provide notifications to affected
            users or relevant authorities in accordance with applicable requirements.
          </p>
        </Section>

        <Section num="23" title="Changes to This Privacy Policy">
          <p>Fanible may update this Privacy Policy from time to time to reflect:</p>
          <Bullets items={[
            "Changes to the Platform", "New features", "Changes in data practices",
            "Changes in applicable laws", "Changes to third-party services",
            "Improvements to our privacy and security practices",
          ]} />
          <p>
            When material changes are made, Fanible may provide notice through the Platform, website, email,
            or another appropriate method where required.
          </p>
          <p>
            The "Last Updated" date at the beginning of this Privacy Policy indicates when the Privacy Policy
            was most recently revised. Your continued use of Fanible after an updated Privacy Policy becomes
            effective constitutes acknowledgment of the updated policy to the extent permitted by applicable
            law.
          </p>
        </Section>

        <Section num="24" title="Contact Us">
          <p>
            If you have questions, concerns, requests, or complaints regarding this Privacy Policy or
            Fanible's handling of personal information, you may contact us.
          </p>
          <div className="bg-white/[0.03] border border-white/8 rounded-2xl p-6 mt-2 space-y-3">
            <p className="text-white font-semibold">Fanible Private Limited</p>
            <div className="space-y-1.5 text-sm">
              <p><span className="text-[#A1A1AA]">Registered Office: </span><span className="text-white">Samastipur, Bihar, India</span></p>
              <p><span className="text-[#A1A1AA]">Website: </span><a href="https://www.fanible.com" className="text-[#3B9EFF] hover:underline">www.fanible.com</a></p>
              <p><span className="text-[#A1A1AA]">Privacy & General Inquiries: </span><a href="mailto:hello@fanible.com" className="text-[#3B9EFF] hover:underline">hello@fanible.com</a></p>
              <p><span className="text-[#A1A1AA]">Support: </span><a href="mailto:support@fanible.com" className="text-[#3B9EFF] hover:underline">support@fanible.com</a></p>
            </div>
            <p className="text-[#A1A1AA] text-xs pt-2 border-t border-white/8">
              For account deletion requests, you may also use the account deletion mechanism provided within
              the Fanible application or the dedicated external account-deletion webpage.
            </p>
          </div>
        </Section>

        <Section num="25" title="Governing Framework">
          <p>
            This Privacy Policy is intended to describe Fanible's privacy and data-handling practices in
            connection with the Platform and should be read together with Fanible's Terms and Conditions.
          </p>
          <p>
            Fanible will handle personal information in accordance with applicable laws and regulations,
            including applicable Indian data-protection requirements and other laws that may apply to the
            Platform or a particular user.
          </p>
          <p>
            If any provision of this Privacy Policy is found to be invalid or unenforceable, the remaining
            provisions will continue to apply to the extent permitted by applicable law.
          </p>
        </Section>

        {/* Footer */}
        <div className="pt-6 border-t border-white/8 flex items-center justify-between">
          <a href="/" className="text-sm text-[#A1A1AA] hover:text-[#3B9EFF] transition-colors font-inter">← Back to Fanible</a>
          <p className="text-[#A1A1AA] text-xs font-inter">© 2026 Fanible Private Limited. All rights reserved.</p>
          <a href="/terms" className="text-sm text-[#A1A1AA] hover:text-white transition-colors font-inter">Terms & Conditions →</a>
        </div>

      </div>
    </div>
  );
}
